import axios from 'axios';

import {Axios} from '@galilee/sdk-core';
import {getAuthorizationJwtHeader, transformFilters} from '@common/helpers/api/apiHelper';
import {StsCredentials} from '@common/types/aws/aws.types';
import {Filters} from '@common/types/components/datatable.types';
import {ActionsResponse, CreateActionResponse, ElasticResponse, EnhancedResponse} from '@common/types/documents/apiResponses.types';
import {CONTENT_TYPE_JSON} from '@common/constants/api/apiConstants';
import {ValueOptionFilters, ValueOptionsApiResponse} from '@common/types/documents/valueOptions.types';
import {UploadFileType} from '../types/documents/fileTypes.types';
import {ApiRequestWithAdditionalParams, ApiRequestWithSectionParams} from '@common/types/documents/apiRequests.types';
import {ApiImportResponse} from '@common/types/components/importDialog.types';

export const apiGetDocuments = async (baseUrl: string, filters: Filters, credentials: StsCredentials, scopedJwt: string): Promise<ElasticResponse> => {
  return new Axios<ElasticResponse, ElasticResponse>()
    .withUrl(baseUrl)
    .withAwsCredentials(credentials)
    .withHeaders(getAuthorizationJwtHeader(scopedJwt))
    .withPathsUrl('documents')
    .withParamsUrl({...transformFilters(filters)})
    .get();
};

export const apiGetDocumentDeps = async (baseUrl: string, filters: Filters, groupedByField: string, scopedJwt: string): Promise<ElasticResponse> => {
  return new Axios<ElasticResponse, ElasticResponse>()
    .withUrl(baseUrl)
    .withHeaders({...getAuthorizationJwtHeader(scopedJwt), CacheSearchDependenciesBreaker: 'cache'})
    .withPathsUrl('documents', 'search-deps')
    .withParamsUrl({...transformFilters(filters), groupedByField})
    .get();
};

export const apiCreateDocument = async (baseUrl: string, data: object, credentials: StsCredentials, scopedJwt: string): Promise<CreateActionResponse> => {
  return new Axios<object, CreateActionResponse>()
    .withUrl(baseUrl)
    .withAwsCredentials(credentials)
    .withHeaders({...getAuthorizationJwtHeader(scopedJwt), 'Content-Type': CONTENT_TYPE_JSON})
    .withPathsUrl('documents')
    .post(data);
};

export const apiUploadFile = async (url: string, file: File): Promise<CreateActionResponse> => {
  const config = {
    headers: {
      'Content-Type': file.type
    }
  };
  return axios.put(url, file, config);
};

// POST request to edit a document (use: documents/{uuid}/actions/edit)
export const apiEditDocument = async (baseUrl: string, uuid: string, data: object, credentials: StsCredentials, scopedJwt: string): Promise<ActionsResponse> => {
  return new Axios<object, ActionsResponse>()
    .withUrl(baseUrl)
    .withAwsCredentials(credentials)
    .withHeaders({...getAuthorizationJwtHeader(scopedJwt), 'Content-Type': CONTENT_TYPE_JSON})
    .withPathsUrl('documents', uuid, 'actions', 'edit')
    .post(data);
};

export const apiDocumentInformation = async (baseUrl: string, uuid: string, data: object, credentials: StsCredentials, scopedJwt: string): Promise<ActionsResponse> => {
  return new Axios<object, ActionsResponse>()
    .withUrl(baseUrl)
    .withAwsCredentials(credentials)
    .withHeaders({...getAuthorizationJwtHeader(scopedJwt), 'Content-Type': CONTENT_TYPE_JSON})
    .withPathsUrl('documents', uuid, 'actions', 'edit-context-information')
    .post(data);
};

export const apiSubmitToApproval = async (baseUrl: string, uuid: string, credentials: StsCredentials, scopedJwt: string): Promise<ActionsResponse> => {
  return new Axios<object, ActionsResponse>()
    .withUrl(baseUrl)
    .withAwsCredentials(credentials)
    .withHeaders({...getAuthorizationJwtHeader(scopedJwt)})
    .withPathsUrl('documents', uuid, 'actions', 'submit-to-approval')
    .post();
};

export const apiSubmitToApprovalWithFile = async (baseUrl: string, uuid: string, data: {files: UploadFileType[]}, credentials: StsCredentials, scopedJwt: string): Promise<ActionsResponse> => {
  return new Axios<object, ActionsResponse>()
    .withUrl(baseUrl)
    .withAwsCredentials(credentials)
    .withHeaders({...getAuthorizationJwtHeader(scopedJwt), 'Content-Type': CONTENT_TYPE_JSON})
    .withPathsUrl('documents', uuid, 'actions', 'submit-to-approval')
    .post(data);
};

export const apiApproverApproveDocument = async (baseUrl: string, uuid: string, credentials: StsCredentials, scopedJwt: string): Promise<ActionsResponse> => {
  return new Axios<object, ActionsResponse>()
    .withUrl(baseUrl)
    .withAwsCredentials(credentials)
    .withHeaders({...getAuthorizationJwtHeader(scopedJwt)})
    .withPathsUrl('documents', uuid, 'actions', 'approver-approve')
    .post();
};

export const apiApproveDocument = async (baseUrl: string, uuid: string, credentials: StsCredentials, scopedJwt: string): Promise<ActionsResponse> => {
  return new Axios<object, ActionsResponse>()
    .withUrl(baseUrl)
    .withAwsCredentials(credentials)
    .withHeaders({...getAuthorizationJwtHeader(scopedJwt)})
    .withPathsUrl('documents', uuid, 'actions', 'approve')
    .post();
};

export const apiApproverRejectDocument = async (baseUrl: string, uuid: string, credentials: StsCredentials, scopedJwt: string, data: {comment: string}): Promise<ActionsResponse> => {
  return new Axios<object, ActionsResponse>()
    .withUrl(baseUrl)
    .withAwsCredentials(credentials)
    .withHeaders({...getAuthorizationJwtHeader(scopedJwt), 'Content-Type': CONTENT_TYPE_JSON})
    .withPathsUrl('documents', uuid, 'actions', 'approver-reject')
    .post(data);
};

export const apiGetValueOptions = async <T>(baseUrl: string, uuid: string, field: string, filters: ValueOptionFilters, credentials: StsCredentials, scopedJwt: string) => {
  return new Axios<object, T>()
    .withUrl(baseUrl)
    .withAwsCredentials(credentials)
    .withHeaders(getAuthorizationJwtHeader(scopedJwt))
    .withParamsUrl(filters)
    .withPathsUrl('documents', uuid, 'fields', field, 'value-options')
    .get();
};

export const apiGetDefaultOptions = async (
  baseUrl: string,
  uuid: string,
  field: string,
  filters: ValueOptionFilters,
  credentials: StsCredentials,
  scopedJwt: string
): Promise<ValueOptionsApiResponse> => {
  return new Axios<ValueOptionsApiResponse, ValueOptionsApiResponse>()
    .withUrl(baseUrl)
    .withAwsCredentials(credentials)
    .withHeaders(getAuthorizationJwtHeader(scopedJwt))
    .withParamsUrl(filters)
    .withPathsUrl('documents', uuid, 'fields', field, 'default-options')
    .get();
};

export const apiGatekeeperApproveDocument = async (baseUrl: string, uuid: string, credentials: StsCredentials, scopedJwt: string): Promise<ActionsResponse> => {
  return new Axios<object, ActionsResponse>()
    .withUrl(baseUrl)
    .withAwsCredentials(credentials)
    .withHeaders({...getAuthorizationJwtHeader(scopedJwt)})
    .withPathsUrl('documents', uuid, 'actions', 'gatekeeper-approve')
    .post();
};

export const apiGatekeeperRejectDocument = async (baseUrl: string, uuid: string, credentials: StsCredentials, scopedJwt: string, data: {comment: string}): Promise<ActionsResponse> => {
  return new Axios<object, ActionsResponse>()
    .withUrl(baseUrl)
    .withAwsCredentials(credentials)
    .withHeaders({...getAuthorizationJwtHeader(scopedJwt), 'Content-Type': CONTENT_TYPE_JSON})
    .withPathsUrl('documents', uuid, 'actions', 'gatekeeper-reject')
    .post(data);
};

export const apiGetDocumentUrlByType = async (baseUrl: string, uuid: string, type: string, credentials: StsCredentials, scopedJwt: string, page?: number): Promise<{url: string}> => {
  return new Axios<{url: string}, {url: string}>()
    .withUrl(baseUrl)
    .withAwsCredentials(credentials)
    .withHeaders(getAuthorizationJwtHeader(scopedJwt))
    .withPathsUrl('documents', uuid, 'files', 'current', type, 'url')
    .withParamsUrl(page ? {page} : {})
    .get();
};

export const apiGetDocumentSection = async ({additionalParamsUrls = {}, baseUrl, uuid, section, credentials, scopedJwt}: ApiRequestWithSectionParams): Promise<EnhancedResponse> => {
  return new Axios<EnhancedResponse, EnhancedResponse>()
    .withUrl(baseUrl)
    .withAwsCredentials(credentials)
    .withHeaders(getAuthorizationJwtHeader(scopedJwt))
    .withPathsUrl('documents', uuid)
    .withParamsUrl({section, ...additionalParamsUrls})
    .get();
};

export const apiGetDocumentActions = async ({baseUrl, uuid, credentials, scopedJwt, additionalParamsUrls = {}}: ApiRequestWithAdditionalParams): Promise<ActionsResponse> => {
  return new Axios<ActionsResponse, ActionsResponse>()
    .withUrl(baseUrl)
    .withAwsCredentials(credentials)
    .withHeaders(getAuthorizationJwtHeader(scopedJwt))
    .withPathsUrl('documents', uuid, 'actions')
    .withParamsUrl({...additionalParamsUrls})
    .get();
};

export const apiGetDocument = async (baseUrl: string, uuid: string, credentials: StsCredentials, scopedJwt: string): Promise<EnhancedResponse> => {
  return new Axios<EnhancedResponse, EnhancedResponse>().withUrl(baseUrl).withAwsCredentials(credentials).withHeaders(getAuthorizationJwtHeader(scopedJwt)).withPathsUrl('documents', uuid).get();
};

export const apiGetApprovalWorkflows = async (baseUrl: string, uuid: string, credentials: StsCredentials, scopedJwt: string): Promise<EnhancedResponse> => {
  return new Axios<EnhancedResponse, EnhancedResponse>()
    .withUrl(baseUrl)
    .withAwsCredentials(credentials)
    .withHeaders(getAuthorizationJwtHeader(scopedJwt))
    .withPathsUrl('documents', uuid, 'approval-workflows')
    .get();
};

export const apiGetDocumentDownloadUrl = async (baseUrl: string, uuid: string, downloadType: string, credentials: StsCredentials, scopedJwt: string) => {
  return new Axios<{url: string}, {url: string}>()
    .withUrl(baseUrl)
    .withAwsCredentials(credentials)
    .withHeaders(getAuthorizationJwtHeader(scopedJwt))
    .withPathsUrl('documents', uuid, 'files', 'current', downloadType, 'url')
    .get();
};

export const apiDeleteDocument = async (baseUrl: string, uuid: string, credentials: StsCredentials, scopedJwt: string): Promise<ActionsResponse> => {
  return new Axios<object, ActionsResponse>()
    .withUrl(baseUrl)
    .withAwsCredentials(credentials)
    .withHeaders({...getAuthorizationJwtHeader(scopedJwt)})
    .withPathsUrl('documents', uuid, 'actions', 'delete')
    .post();
};

export const apiExportDocument = async (baseUrl: string, exportKey: string, actionKey: string, data: Filters = {}, credentials: StsCredentials, scopedJwt: string): Promise<ActionsResponse> => {
  return new Axios<object, ActionsResponse>()
    .withUrl(baseUrl)
    .withAwsCredentials(credentials)
    .withHeaders({...getAuthorizationJwtHeader(scopedJwt), 'Content-Type': CONTENT_TYPE_JSON})
    .withPathsUrl('documents', exportKey, 'actions', actionKey)
    .withErrorsHandler(e => {
      throw new Error(e.message);
    })
    .post(data);
};

export const apiManageCsbusDocument = async (baseUrl: string, uuid: string, data: {csbus: string[]}, credentials: StsCredentials, scopedJwt: string): Promise<ActionsResponse> => {
  return new Axios<object, ActionsResponse>()
    .withUrl(baseUrl)
    .withAwsCredentials(credentials)
    .withHeaders({...getAuthorizationJwtHeader(scopedJwt), 'Content-Type': CONTENT_TYPE_JSON})
    .withPathsUrl('documents', uuid, 'actions', 'manage-related-csbus')
    .post(data);
};

export const apiImportDocument = async (
  baseUrl: string,
  uuid: string,
  actionKey: string,
  data: {files: {path: string; name: string}[]},
  isDryRunRequest: boolean,
  credentials: StsCredentials,
  scopedJwt: string
): Promise<ApiImportResponse> => {
  return new Axios<object, ApiImportResponse>()
    .withUrl(baseUrl)
    .withAwsCredentials(credentials)
    .withHeaders({...getAuthorizationJwtHeader(scopedJwt), 'Content-Type': CONTENT_TYPE_JSON})
    .withPathsUrl('documents', uuid, 'actions', actionKey)
    .withParamsUrl(isDryRunRequest ? {'dry-run': isDryRunRequest} : {})
    .withErrorsHandler(e => {
      throw new Error(e.message);
    })
    .post(data);
};

export const apiPutInDraft = async (baseUrl: string, uuid: string, credentials: StsCredentials, scopedJwt: string) => {
  return new Axios<object, {transactionId: string}>()
    .withUrl(baseUrl)
    .withAwsCredentials(credentials)
    .withHeaders({...getAuthorizationJwtHeader(scopedJwt)})
    .withPathsUrl('documents', uuid, 'actions', 'put-in-draft')
    .post();
};

export const apiReviewerApproveDocument = async (baseUrl: string, uuid: string, credentials: StsCredentials, scopedJwt: string): Promise<ActionsResponse> => {
  return new Axios<object, ActionsResponse>()
    .withUrl(baseUrl)
    .withAwsCredentials(credentials)
    .withHeaders({...getAuthorizationJwtHeader(scopedJwt)})
    .withPathsUrl('documents', uuid, 'actions', 'reviewer-approve')
    .post();
};

export const apiReviewerRejectDocument = async (baseUrl: string, uuid: string, credentials: StsCredentials, scopedJwt: string, data: {comment: string}): Promise<ActionsResponse> => {
  return new Axios<object, ActionsResponse>()
    .withUrl(baseUrl)
    .withAwsCredentials(credentials)
    .withHeaders({...getAuthorizationJwtHeader(scopedJwt), 'Content-Type': CONTENT_TYPE_JSON})
    .withPathsUrl('documents', uuid, 'actions', 'reviewer-reject')
    .post(data);
};

export const apiMakeAvailable = async (baseUrl: string, uuid: string, credentials: StsCredentials, scopedJwt: string) => {
  return new Axios<object, ActionsResponse>()
    .withUrl(baseUrl)
    .withAwsCredentials(credentials)
    .withHeaders({...getAuthorizationJwtHeader(scopedJwt), 'Content-Type': CONTENT_TYPE_JSON})
    .withPathsUrl('documents', uuid, 'actions', 'make-available')
    .post();
};

export const apiCreateNewRevision = async (baseUrl: string, uuid: string, newFile: UploadFileType, credentials: StsCredentials, scopedJwt: string) => {
  const data = {files: [newFile]};
  return new Axios<object, ActionsResponse>()
    .withUrl(baseUrl)
    .withAwsCredentials(credentials)
    .withHeaders({...getAuthorizationJwtHeader(scopedJwt), 'Content-Type': CONTENT_TYPE_JSON})
    .withPathsUrl('documents', uuid, 'actions', 'new-revision')
    .post(data);
};

export const apiUploadRevision = async (baseUrl: string, uuid: string, newFile: UploadFileType, credentials: StsCredentials, scopedJwt: string) => {
  const data = {files: [newFile]};
  return new Axios<object, ActionsResponse>()
    .withUrl(baseUrl)
    .withAwsCredentials(credentials)
    .withHeaders({...getAuthorizationJwtHeader(scopedJwt), 'Content-Type': CONTENT_TYPE_JSON})
    .withPathsUrl('documents', uuid, 'actions', 'upload-revision')
    .post(data);
};

export const apiInvalidateDocument = async (baseUrl: string, uuid: string, credentials: StsCredentials, scopedJwt: string, data: {comment: string}) => {
  return new Axios<object, ActionsResponse>()
    .withUrl(baseUrl)
    .withAwsCredentials(credentials)
    .withHeaders({...getAuthorizationJwtHeader(scopedJwt), 'Content-Type': CONTENT_TYPE_JSON})
    .withPathsUrl('documents', uuid, 'actions', 'invalidate')
    .post(data);
};

export const apiMarkAsInvoiced = async (baseUrl: string, uuid: string, credentials: StsCredentials, scopedJwt: string, data: {puNumber: string}) => {
  return new Axios<object, ActionsResponse>()
    .withUrl(baseUrl)
    .withAwsCredentials(credentials)
    .withHeaders({...getAuthorizationJwtHeader(scopedJwt), 'Content-Type': CONTENT_TYPE_JSON})
    .withPathsUrl('documents', uuid, 'actions', 'mark-as-invoiced')
    .post(data);
};

export const apiMarkAsDelivered = async (baseUrl: string, uuid: string, credentials: StsCredentials, scopedJwt: string) => {
  return new Axios<object, ActionsResponse>()
    .withUrl(baseUrl)
    .withAwsCredentials(credentials)
    .withHeaders({...getAuthorizationJwtHeader(scopedJwt), 'Content-Type': CONTENT_TYPE_JSON})
    .withPathsUrl('documents', uuid, 'actions', 'mark-as-delivered')
    .post();
};
